/**
 * "My reports" — `/me/feedback`, a shell route (bata-platform#686, ADR 9063; the lifecycle of
 * bata-platform ADR 1020, ADR 9071).
 *
 * What the signed-in person has reported from this application — a question, a figure to check, a
 * malfunction or an enhancement request — and where each stands. A row opens the report in a
 * drawer: what was sent, every move it made, and the thread with the delivery team, with a box to
 * reply while it is open. A report on which the team asked something says «Your reply is needed»
 * in its row, so the person sees it without opening each one.
 *
 * The team's internal notes and identities are never shown: the history says «The team», not who.
 * Nothing here links outside the application.
 *
 * A shell route for the Profile's reason (ADR 9012): the list spans every chapter and belongs to
 * none. The reads are in `../../feedback/api.ts`, the one place that knows the endpoints.
 */

import { MessageSquare } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDateTime } from '../../../lib/format';
import { AsyncRegion, DataTable, PageStatusRegion, Panel, type DataColumn } from '../../../planning';
import { Button, Drawer } from '../../../primitives';

import { useMyFeedback, type FeedbackReport } from '../../feedback/api';
import { ReportStatus } from '../../feedback/ReportStatus';
import { kindWords, statusWords, useSectionLabel } from '../../feedback/words';

import { MyReportDetail } from './MyReportDetail';

export function MyReportsScreen(): React.JSX.Element {
  return (
    <PageStatusRegion>
      <MyReports />
    </PageStatusRegion>
  );
}

function MyReports(): React.JSX.Element {
  const { t } = useTranslation();
  const reports = useMyFeedback();
  const [open, setOpen] = useState<string | null>(null);
  return (
    <>
      <AsyncRegion
        query={reports}
        errorTitleKey="feedback.reports.failed"
        emptyTitleKey="feedback.reports.empty.title"
        emptyBodyKey="feedback.reports.empty.body"
        isEmpty={(data) => data.length === 0}
        enclosingTitleKey="feedback.reports.panel"
        loadingRows={4}
      >
        {(data) => (
          <Panel titleKey="feedback.reports.panel" subtitleKey="feedback.reports.subtitle">
            <ReportsTable reports={data} onOpen={setOpen} />
            <p className="text-caption text-ink-secondary mt-2">{t('feedback.reports.privacy')}</p>
          </Panel>
        )}
      </AsyncRegion>
      <Drawer
        open={open !== null}
        onOpenChange={(next) => { if (!next) setOpen(null); }}
        titleKey="feedback.triage.detail.title"
        titleValues={{ code: open ?? '' }}
      >
        {open !== null && <MyReportDetail key={open} code={open} />}
      </Drawer>
    </>
  );
}

function ReportsTable({ reports, onOpen }: {
  readonly reports: readonly FeedbackReport[];
  readonly onOpen: (code: string) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const sectionLabel = useSectionLabel();
  // Newest first: the one just sent is the one the person came to look for.
  const rows = [...reports].sort((a, b) => b.created_at.localeCompare(a.created_at));
  const columns: readonly DataColumn<FeedbackReport>[] = [
    {
      id: 'code', headerKey: 'feedback.reports.col.code', descriptionKey: 'help.feedbackReports.code', kind: 'text', code: true,
      value: (row) => row.code,
      cell: (row) => <th scope="row" className="py-1 pe-4 text-start font-numeric font-semibold" translate="no">{row.code}</th>,
    },
    {
      id: 'date', headerKey: 'feedback.reports.col.date', descriptionKey: 'help.feedbackReports.date', kind: 'date',
      value: (row) => row.created_at,
      cell: (row) => (
        <td className="py-1 pe-4 whitespace-nowrap font-numeric">
          <time dateTime={row.created_at}>{t('feedback.reports.at', { time: formatDateTime(row.created_at, i18n.language, 'UTC') })}</time>
        </td>
      ),
    },
    {
      id: 'kind', headerKey: 'feedback.reports.col.kind', descriptionKey: 'help.feedbackReports.kind', kind: 'enum',
      value: (row) => row.kind,
      label: (value) => kindWords(t, String(value)),
      cell: (row) => <td className="py-1 pe-4 whitespace-nowrap">{kindWords(t, row.kind)}</td>,
    },
    {
      id: 'section', headerKey: 'feedback.reports.col.section', descriptionKey: 'help.feedbackReports.section', kind: 'text',
      value: (row) => sectionLabel(row.section_id),
      cell: (row) => <td className="py-1 pe-4">{sectionLabel(row.section_id)}</td>,
    },
    {
      id: 'title', headerKey: 'feedback.reports.col.title', descriptionKey: 'help.feedbackReports.title', kind: 'text',
      value: (row) => row.title,
      // The person's own words: not translated, whatever the interface language.
      cell: (row) => <td className="py-1 pe-4" translate="no">{row.title}</td>,
    },
    {
      // The team's answer to the author, read-only. Wrapped, not cut: it is the point of the page.
      id: 'answer', headerKey: 'feedback.reports.col.answer', descriptionKey: 'help.feedbackReports.answer', kind: 'text',
      value: (row) => row.resolution_note ?? null,
      cell: (row) => (
        <td className="max-w-[32rem] py-1 pe-4 align-top whitespace-pre-line" data-resolution-note="">
          {row.resolution_note === undefined || row.resolution_note === null || row.resolution_note === ''
            ? <span className="text-ink-secondary">{t('feedback.reports.noAnswer')}</span>
            : row.resolution_note}
        </td>
      ),
    },
    {
      id: 'status', headerKey: 'feedback.reports.col.status', descriptionKey: 'help.feedbackReports.status', kind: 'enum', emphasis: true,
      value: (row) => row.status,
      label: (value) => statusWords(t, String(value)),
      cell: (row) => (
        <td className="py-1 pe-4 whitespace-nowrap">
          <span className="flex flex-col items-start gap-1">
            <ReportStatus status={row.status} />
            {row.awaiting_your_reply === true && (
              <span className="text-caption text-ink-primary" data-awaiting-reply="">{t('feedback.reports.awaitingYou')}</span>
            )}
          </span>
        </td>
      ),
    },
    {
      id: 'actions', headerKey: 'feedback.triage.col.actions', descriptionKey: 'help.feedbackReports.actions',
      cell: (row) => (
        <td className="py-1 text-end">
          <Button
            variant="ghost"
            size="sm"
            icon={<MessageSquare size={13} aria-hidden />}
            onClick={() => onOpen(row.code)}
            aria-label={t('feedback.triage.openReport', { code: row.code })}
            data-tour="feedback-my-report-open"
          >
            {t('feedback.triage.open')}
          </Button>
        </td>
      ),
    },
  ];
  return (
    <DataTable
      sourceKey="help.feedbackReports.source"
      density
      tourAnchor="feedback-my-reports-table"
      rows={rows}
      rowKey={(row) => row.code}
      columns={columns}
      captionKey="feedback.reports.panel"
      captionHidden
      exportId="me.feedback"
      exportTitleKey="feedback.reports.panel"
    />
  );
}
